import { Button, ButtonGroup, DateInput, Field, IMessageProps, Spinner, TextOutput } from 'Components/Core';
import { ListFieldSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { Message } from 'Data/ViewModels/IApiDataModel';
import { hasNonEmptyValue, hasValue, valueOrDefault } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { combine } from 'Lib/Hooks/Selectors';
import moment from 'moment';
import { pipe } from 'ramda';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as amddActions } from '../../../../../Data/ReduxViewModels/AssignedValuesTests';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import AssignedMaximumDryDensityInjectedPropsProvider from '../../AssignedMaximumDryDensityInjectedPropsProvider';
import { AssignedValueTest, MaximumDryDensityTest, maxTestsToSelect } from '../../types';
let AsAssignValuesSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dispatch = useDispatch();
  const creating = useSelector((state) => state.assignedMaximumDryDensityTests.status.creating) as boolean;
  const calculating = useSelector((state) => state.assignedMaximumDryDensityTests.status.calculating);
  const assignedDate = useSelector((state) => state.assignedMaximumDryDensityTests?.assignedDate) as Date;
  const isAssignedDateBeforeLatest = useSelector(
    pipe(
      createWorksheetSelector!('AssignedValueTests'),
      (assignedValueTests: AssignedValueTest[]) => assignedValueTests?.at(-1)?.DateAssigned,
      (latestDate) =>
        hasNonEmptyValue(assignedDate) &&
        hasNonEmptyValue(latestDate) &&
        moment(assignedDate).format('YYYY-MM-DD') <= moment(latestDate).format('YYYY-MM-DD')
    )
  ) as boolean;
  /**
   * Variable to indicate if the maximum amount of tests have been selected (6)
   */
  const maxTestsAreSelected: boolean = useSelector((state) =>
    pipe(
      (state) => state.assignedMaximumDryDensityTests.mddTests?.Worksheets,
      valueOrDefault([]),
      (mddTests: MaximumDryDensityTest[]) => mddTests?.filter((row) => row.selected)?.length === maxTestsToSelect
    )(state)
  );

  const allSelectedTestsHaveSameOversizeSieveAperture = useSelector(
    pipe(
      (state) => state.assignedMaximumDryDensityTests.mddTests?.Worksheets ?? [],
      (mddTests: MaximumDryDensityTest[]) => mddTests?.filter((row) => row.selected),
      (selectedTests: MaximumDryDensityTest[]) =>
        !maxTestsAreSelected ||
        selectedTests?.every((row) => row.oversizeSieveApertureSizeId === selectedTests[0].oversizeSieveApertureSizeId)
    )
  ) as boolean;

  const getAssignValuesButtonMessages = () => {
    let assignValuesButtonMessages: IMessageProps[] = [];
    if (maxTestsAreSelected && isAssignedDateBeforeLatest) {
      assignValuesButtonMessages.push({
        severity: 'error',
        message: 'New date assigned should be after the last date assigned.',
      });
    }
    if (!hasValue(assignedDate) && maxTestsAreSelected) {
      assignValuesButtonMessages.push({
        severity: 'error',
        message: 'Date assigned is required.',
      });
    }
    return assignValuesButtonMessages;
  };

  const disableAssignButton = useSelector(
    combine(
      [
        (state) => state.assignedMaximumDryDensityTests.mddTests?.Worksheets,
        (state) => state.assignedMaximumDryDensityTests.results?.Messages,
        createWorksheetSelector!('ValueAssignment.LatestSourceEntitySampledDate'),
      ],
      (mddWorksheets, resultsMessages, latestSampledDate) => {
        const latestMddDateSampled = mddWorksheets?.filter((row) => row.selected)[0]?.dateSampled as Date;

        return (
          !maxTestsAreSelected ||
          !hasNonEmptyValue(assignedDate) ||
          !allSelectedTestsHaveSameOversizeSieveAperture ||
          isAssignedDateBeforeLatest ||
          resultsMessages?.some((m: Message) => m.MessageType === 'error') ||
          (hasValue(latestMddDateSampled) && hasValue(latestSampledDate) && latestMddDateSampled < latestSampledDate)
        );
      }
    )
  ) as boolean;

  return (
    <AssignedMaximumDryDensityInjectedPropsProvider>
      <ListFieldSection heading="Results">
        {calculating ? (
          <div style={{ position: 'relative', height: '15vh' }}>
            <Spinner large />
          </div>
        ) : (
          <>
            <Field
              input={<TextOutput />}
              label={'Mean Maximum Dry Density (t/m³)'}
              name="results.MeanMaximumDryDensity"
              format={{ uom: 't/m³', nearest: 0.001 }}
            />
            <Field
              input={<TextOutput />}
              label={'Greatest Difference From Mean (t/m³)'}
              name="results.GreatestDifferenceFromMeanMdd"
              format={{ nearest: 0.001 }}
            />
            <Field
              input={<TextOutput />}
              label={'Mean Optimum Moisture Content (%)'}
              name="results.MeanOptimumMoistureContent"
              format={{ nearest: 0.01 }}
            />
            <Field
              input={<TextOutput />}
              label={'Greatest Difference From Mean (%)'}
              name="results.GreatestDifferenceFromMeanOmc"
              format={{ nearest: 0.01 }}
            />
          </>
        )}
      </ListFieldSection>
      <ListFieldSection heading="Assign Values">
        <Field
          messages={getAssignValuesButtonMessages()}
          input={<DateInput />}
          label="Date Assigned"
          dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
          name={'assignedDate'}
        />
      </ListFieldSection>
      <ButtonGroup>
        <Button
          loading={creating}
          disabled={disableAssignButton}
          primary
          onClick={() => dispatch(amddActions.createAssignedValueTest())}
        >
          Assign Values
        </Button>
      </ButtonGroup>
    </AssignedMaximumDryDensityInjectedPropsProvider>
  );
};

AsAssignValuesSection = withInjectedProps(AsAssignValuesSection);

export { AsAssignValuesSection };
export default AsAssignValuesSection;
